import React from "react";
import {
  Box,
  Select,
  MenuItem,
  Button,
  IconButton,
  FormControl,
  ListItemIcon,
  ListItemText,
  SelectChangeEvent,
} from "@mui/material";
import {
  Analytics as AnalyticsIcon,
  Chat as ChatIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from "@mui/icons-material";
import { useAppSelector, useAppDispatch } from "../../store";
import { setSelectedId } from "../../store/slices/scenariosSlice";
import { startAnalysis } from "../../store/slices/analysisSlice";
import { openChatSidebar } from "../../store/slices/uiSlice";



const ActionBar: React.FC = () => {
  const dispatch = useAppDispatch();
  const { deviations, selectedId, analyzedIds } = useAppSelector(
    (state) => state.scenarios,
  );
  const { isRunning } = useAppSelector((state) => state.analysis);

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    dispatch(setSelectedId(event.target.value));
  };

  const handleAnalyze = () => {
    if (selectedId) {
      dispatch(startAnalysis({ scenarioId: selectedId }));
    }
  };

  const handleOpenChat = () => {
    dispatch(openChatSidebar());
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: "10px",
        alignItems: "center",
        marginBottom: "14px",
        backgroundColor: "background.paper",
        padding: "12px 16px",
        borderRadius: "10px",
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
      }}
    >
      <FormControl sx={{ flex: 1 }}>
        <Select
          value={selectedId || ""}
          onChange={handleSelectChange}
          displayEmpty
          sx={{
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            "& .MuiSelect-select": {
              padding: "10px 14px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            },
          }}
          renderValue={(value) => {
            const deviation = deviations.find((d) => d.id === value);
            const isAnalyzed = analyzedIds.includes(value);
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {isAnalyzed ? (
                  <CheckBoxIcon sx={{ color: "success.main", fontSize: 20 }} />
                ) : (
                  <CheckBoxOutlineBlankIcon
                    sx={{ color: "#9e9e9e", fontSize: 20 }}
                  />
                )}
                <span style={{ fontWeight: 500, fontSize: "13px" }}>
                  {deviation
                    ? deviation.title
                    : "Select a deviation"}
                </span>
              </Box>
            );
          }}
        >
          {deviations.map((deviation) => {
            const isAnalyzed = analyzedIds.includes(deviation.id);
            return (
              <MenuItem key={deviation.id} value={deviation.id}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {isAnalyzed ? (
                    <CheckBoxIcon
                      sx={{ color: "success.main", fontSize: 20 }}
                    />
                  ) : (
                    <CheckBoxOutlineBlankIcon
                      sx={{ color: "#9e9e9e", fontSize: 20 }}
                    />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={deviation.title}
                  primaryTypographyProps={{
                    fontSize: "13px",
                    fontWeight: 500,
                  }}
                />
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      <Button
        variant="contained"
        color="primary"
        startIcon={<AnalyticsIcon sx={{ fontSize: 18 }} />}
        onClick={handleAnalyze}
        disabled={!selectedId || isRunning}
        sx={{
          padding: "10px 24px",
          fontSize: "13px",
          fontWeight: 500,
        }}
      >
        {isRunning ? "Analyzing..." : "Analyze"}
      </Button>

      <IconButton
        onClick={handleOpenChat}
        sx={{
          backgroundColor: "secondary.main",
          color: "#fff",
          padding: "10px",
          borderRadius: "8px",
          "&:hover": {
            backgroundColor: "secondary.dark",
            boxShadow: "0 4px 12px rgba(255,152,0,0.35)",
          },
        }}
      >
        <ChatIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
};

export default ActionBar;
